import { Button } from '@sleepstation/components'
import { BreakingLayout, VLayout } from '@sleepstation/components-layouts'
import { CopyRenderer } from '@sleepstation/components-markdown-renderer'
import {
  DIRECTION,
  LAYOUT_ALIGN,
  LAYOUT_SPACING,
  SIZE,
} from '@sleepstation/const'
import { parseToAst } from '@sleepstation/markdown'
import {
  backgroundColor,
  baseline,
  borderRadius,
  bottom,
  left,
  maxWidth,
  mq,
  padding,
  width,
} from '@sleepstation/style-api-helpers'
import { dropShadow } from '@sleepstation/style-mixins'
import {
  defaultAltTheme,
  defaultSecondaryTheme,
} from '@sleepstation/themes-public-site'
import { isRunningInBrowser, yearsInSeconds } from '@sleepstation/utils'
import PropTypes from 'prop-types'
import { isNotNil } from 'ramda-adjunct'
import React, { useState } from 'react'
import { Cookies, withCookies } from 'react-cookie'
import styled, { ThemeProvider } from 'styled-components'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const BUTTON_LABEL = `OK`

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const handleClick = (cookies, cookieName, domain, setIsConsented) => () => {
  cookies.set(cookieName, true, {
    path: `/`,
    maxAge: yearsInSeconds(1),
    secure: true,
    domain,
  })

  setIsConsented(true)
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Wrapper = styled.div`
  position: fixed;
  ${backgroundColor(`background`)}
  /* This is one greater than the Intercom button's z-index */
  z-index: 2147483002;
  left: 0;
  right: 0;
  bottom: 0;
  ${padding([[0.75, 1], [1, 1.25], 0.75])}
  box-shadow: 0 2px 6px 0 rgba(0 0 0 / 50%);

  ${mq(`mediumUp`)} {
    ${width(10.75)}
    ${left(1)}
    ${bottom(1)}
		${borderRadius(0.5)}
    ${dropShadow()}
  	right: auto;
    border-top: none;
  }
`

const Layout = styled(BreakingLayout).attrs({
  spacing: [
    LAYOUT_SPACING.MEDIUM,
    LAYOUT_SPACING.X_LARGE,
    LAYOUT_SPACING.MEDIUM,
  ],
  align: [LAYOUT_ALIGN.CENTER],
  direction: [DIRECTION.VERTICAL, DIRECTION.HORIZONTAL, DIRECTION.VERTICAL],
  'data-cy': `cookie-consent-notice`,
})``

const Primary = styled(VLayout).attrs({
  spacing: 0,
  align: LAYOUT_ALIGN.LEFT,
})`
  p,
  a {
    ${baseline([
      [-1, 1],
      [-1, 1],
      [-1, 1],
    ])}
  }
`

const Secondary = styled.div`
  align-self: center;

  ${mq(`xSmallOnly`)} {
    width: 100%;
    ${maxWidth(10)}
  }

  ${mq(`smallOnly`)} {
    ${width(12)}
  }

  ${mq(`mediumUp`)} {
    align-self: stretch;
  }

  > * {
    width: 100%;
  }
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const CookieConsentNotice = ({ cookies, cookieName, body, domain }) => {
  const [consented, setIsConsented] = useState(false)

  if (consented || !isRunningInBrowser()) return ``

  const bodyAst = parseToAst(body)
  const cookie = cookies.get(cookieName)

  if (!consented && isNotNil(cookie)) {
    setIsConsented(true)
  }

  return (
    <ThemeProvider theme={defaultSecondaryTheme}>
      <Wrapper>
        <Layout>
          <Primary>
            <CopyRenderer htmlAst={bodyAst} />
          </Primary>
          <Secondary>
            <ThemeProvider theme={defaultAltTheme}>
              <Button
                data-cy="cookie-consent-button"
                onClick={handleClick(
                  cookies,
                  cookieName,
                  domain,
                  setIsConsented
                )}
                kind="secondary"
                size={SIZE.X_SMALL}
              >
                {BUTTON_LABEL}
              </Button>
            </ThemeProvider>
          </Secondary>
        </Layout>
      </Wrapper>
    </ThemeProvider>
  )
}

CookieConsentNotice.propTypes = {
  cookies: PropTypes.instanceOf(Cookies).isRequired,
  cookieName: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  domain: PropTypes.string.isRequired,
}

export default withCookies(CookieConsentNotice)
